<template>
  <div class="pick-embed" v-loading="saving">
    <div class="pick-embed__title">
      <a href="javascript:;" title="点击可查看受理信息" @click.prevent="$emit('open-project', project)">
        {{ project.ProjectName }}
        <span class="text-muted">{{ project.ProjectType }}</span>
      </a>
    </div>

    <div class="pick-embed__batch" v-if="!readonly">
      <span class="text-muted">勾选任务并设置：</span>
      <button class="btn btn-link btn-sm" type="button" @click="openMembers">采样组员</button>
      <button class="btn btn-link btn-sm" type="button" @click="openDate">采样日期</button>
      <button class="btn btn-link btn-sm" type="button" @click="openInst">采样仪器</button>
      <button class="btn btn-link btn-sm" type="button" @click="openVehicle">车辆安排</button>
      <button class="btn btn-sm btn-primary" type="button" title="将选择的采样任务发给下一步" @click="submitSelected">
        <i class="fa fa-sign-in" /> 提交
      </button>
    </div>

    <table class="table table-bordered table-striped pick-embed__table">
      <thead>
        <tr>
          <th v-if="!readonly" style="width:36px;">
            <input type="checkbox" :checked="allSelected" @change="toggleAll" />
          </th>
          <th>项目类型</th>
          <th>监测点位</th>
          <th>采样人</th>
          <th>采样日期</th>
          <th>采样仪器</th>
          <th>车牌号</th>
          <th>状态</th>
          <th style="width:88px;">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in displayRows" :key="row.id">
          <td v-if="!readonly">
            <input
              type="checkbox"
              v-model="row.selected"
              :disabled="isArrangeLocked(row)"
              :title="isArrangeLocked(row) ? '已完成采样，不可再改安排' : ''"
            />
          </td>
          <td>
            <div>{{ row.inspectSubType || '—' }}</div>
            <div class="text-muted small" style="max-width:280px;word-break:break-all;">{{ row.inspectItems }}</div>
            <div class="text-muted small">{{ row.frequency }}</div>
          </td>
          <td>{{ row.monitorPlace || '—' }}</td>
          <td>
            <div v-if="row.pickCharge">{{ row.pickCharge }}</div>
            <div class="text-muted small">{{ row.pickMembers || row.pickStaff || '—' }}</div>
          </td>
          <td>{{ row.pickDateLabel || '—' }}</td>
          <td>
            <span class="pick-embed__cell-wrap" :title="row.instruments || ''">{{ row.instruments || '—' }}</span>
          </td>
          <td>{{ row.vehiclePlate || '—' }}</td>
          <td>{{ rowStatus(row) }}</td>
          <td>
            <button
              v-if="canAudit(row)"
              class="btn btn-link btn-sm"
              type="button"
              title="审核 App 提交的采样信息"
              @click="openPickAudit(row)"
            >
              审核
            </button>
            <span v-else class="text-muted">—</span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="!displayRows.length" class="text-center text-muted" style="padding:24px;">该项目暂无采样任务</div>

    <el-dialog
      title="设置采样组员"
      :visible.sync="memberDlg"
      width="480px"
      append-to-body
      custom-class="yn-pick-dlg"
      @closed="onPickDlgClosed"
    >
      <el-select
        ref="memberSelect"
        v-model="batchMembers"
        multiple
        filterable
        clearable
        placeholder="选择组员"
        style="width:100%"
        :popper-append-to-body="false"
      >
        <el-option v-for="u in users" :key="u.id" :label="u.xm" :value="String(u.id)" />
      </el-select>
      <div slot="footer">
        <el-button @click="memberDlg=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="confirmMembers">确定</el-button>
      </div>
    </el-dialog>
    <el-dialog
      title="设置采样日期"
      :visible.sync="dateDlg"
      width="420px"
      append-to-body
      custom-class="yn-pick-dlg"
      @closed="onPickDlgClosed"
    >
      <el-date-picker
        v-model="batchDate"
        type="daterange"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        style="width:100%"
        :append-to-body="false"
      />
      <div slot="footer">
        <el-button @click="dateDlg=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="confirmDate">确定</el-button>
      </div>
    </el-dialog>
    <el-dialog
      title="车辆安排 · 选择车牌"
      :visible.sync="vehicleDlg"
      width="420px"
      append-to-body
      custom-class="yn-pick-dlg"
      @closed="onPickDlgClosed"
    >
      <el-select
        ref="vehicleSelect"
        v-model="batchPlate"
        filterable
        allow-create
        default-first-option
        clearable
        placeholder="选择或输入车牌号"
        style="width:100%"
        :popper-append-to-body="false"
      >
        <el-option v-for="p in plateOptions" :key="p" :label="p" :value="p" />
      </el-select>
      <p class="text-muted" style="margin:10px 0 0;font-size:12px;">可从列表选择，也可直接输入自定义车牌</p>
      <div slot="footer">
        <el-button @click="vehicleDlg=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="confirmVehicle">确定</el-button>
      </div>
    </el-dialog>
    <PickInstrumentPicker v-model="instDlg" @pick="onInstPicked" />
  </div>
</template>

<script>
import api from '@/utils/api'
import { isPickSampler } from '@/utils/user'
import { patchPickFields, markPickSubmitted, PICK_VEHICLE_PLATES, loadPickOverrides, formatPickedInstruments, flushPickArrangeToServer, validatePickArrangeReady, isPickArrangeLocked } from '@/utils/pickAdapter'
import PickInstrumentPicker from '@/components/ivan/PickInstrumentPicker.vue'
import { pingTodoRefresh } from '@/utils/todoSync'

export default {
  name: 'PickTaskEmbedPanel',
  components: { PickInstrumentPicker },
  props: {
    project: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      saving: false,
      users: [],
      /** taskId → pending_audit 记录，App 提交后才可审 */
      pendingByTask: {},
      /** taskId → audited 记录 */
      auditedByTask: {},
      /** 对不上监测项的待审/已审孤儿行（稳定引用，避免勾选态丢失） */
      orphanPendingRows: [],
      memberDlg: false,
      dateDlg: false,
      instDlg: false,
      vehicleDlg: false,
      batchMembers: [],
      batchDate: null,
      batchPlate: '',
      plateOptions: PICK_VEHICLE_PLATES.slice(),
      /** 打开批量弹窗时锁定的行 id，避免 softReload 清勾选后确认落空 */
      batchTargetIds: [],
      pendingTimer: null
    }
  },
  computed: {
    selected() {
      return (this.displayRows || []).filter(r => r.selected)
    },
    editableRows() {
      return (this.displayRows || []).filter(r => r && !this.isArrangeLocked(r))
    },
    allSelected() {
      const list = this.editableRows
      return list.length > 0 && list.every(r => r.selected)
    },
    /** 合并 App 待审孤儿行；待审核置顶 */
    displayRows() {
      const base = this.rows || []
      const seen = {}
      base.forEach(r => { if (r && r.id) seen[String(r.id)] = true })
      const extras = (this.orphanPendingRows || []).filter(r => r && r.id && !seen[String(r.id)])
      const all = extras.length ? base.concat(extras) : base.slice()
      return all.slice().sort((a, b) => {
        const pa = this.canAudit(a) ? 0 : 1
        const pb = this.canAudit(b) ? 0 : 1
        return pa - pb
      })
    }
  },
  mounted() {
    this.loadUsers()
    this.loadPendingAudit()
    this.startPendingWatch()
    this.flushArrange()
  },
  activated() {
    this.loadPendingAudit()
    this.startPendingWatch()
    this.flushArrange()
  },
  deactivated() {
    this.stopPendingWatch()
  },
  beforeDestroy() {
    this.stopPendingWatch()
  },
  watch: {
    project() {
      this.loadPendingAudit()
    },
    rows: {
      handler() {
        this.flushArrange()
      },
      deep: false
    }
  },
  methods: {
    async flushArrange() {
      try {
        await flushPickArrangeToServer(this.rows || [])
      } catch (e) { /* ignore */ }
    },
    startPendingWatch() {
      this.stopPendingWatch()
      // App 提交后 PC 端不离页也要能看到「待审核」——只拉待审映射，不整表换行
      this._pendingTimer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
        if (this.isBatchDialogOpen()) return
        this.loadPendingAudit()
      }, 2000)
      this._onPendingVis = () => {
        if (document.visibilityState === 'visible') {
          if (this.isBatchDialogOpen()) return
          this.loadPendingAudit()
        }
      }
      this._onPendingFocus = () => {
        if (this.isBatchDialogOpen()) return
        this.loadPendingAudit()
      }
      document.addEventListener('visibilitychange', this._onPendingVis)
      window.addEventListener('focus', this._onPendingFocus)
    },
    stopPendingWatch() {
      if (this._pendingTimer) {
        clearInterval(this._pendingTimer)
        this._pendingTimer = null
      }
      if (this._onPendingVis) {
        document.removeEventListener('visibilitychange', this._onPendingVis)
        this._onPendingVis = null
      }
      if (this._onPendingFocus) {
        window.removeEventListener('focus', this._onPendingFocus)
        this._onPendingFocus = null
      }
    },
    toggleAll(e) {
      const v = !!(e.target && e.target.checked)
      ;(this.editableRows || []).forEach(r => { r.selected = v })
      // 已完成采样的行始终取消勾选
      ;(this.displayRows || []).forEach(r => {
        if (this.isArrangeLocked(r)) r.selected = false
      })
    },
    isArrangeLocked(row) {
      if (isPickArrangeLocked(row)) return true
      return this.isAudited(row)
    },
    isAudited(row) {
      if (!row) return false
      if (row.pickStatus === 'audited' || row.status === '已审核' || row.status === '已完成采样' || row.status === '样品交接') return true
      return !!(this.auditedByTask && this.auditedByTask[row.id])
    },
    /** App 已提交待审 → 才可审核；已审不可再审 */
    canAudit(row) {
      if (!row || this.isAudited(row)) return false
      if (this.pendingByTask[row.id]) return true
      if (row.pickStatus === 'pending_audit') return true
      if (row.status === '待审核') return true
      return false
    },
    rowStatus(row) {
      // 现场部视角：审完 = 已完成采样（样品管理归综合部主任 PC 菜单，不在此列展示）
      if (this.isAudited(row)) return '已完成采样'
      if (this.pendingByTask[row.id] || row.pickStatus === 'pending_audit' || row.status === '待审核') {
        return '待审核'
      }
      // 「提交至采样提交」后进入现场采样，不是审批
      if (row.status === '已提交' || row.status === '采样中') return '采样中'
      if (row.status === '已完成' || row.status === '已完成采样') return '已完成采样'
      return row.status || '待安排采样'
    },
    openHandover(row) {
      const p = this.project || {}
      const xmbh = String(p.xmbh || p.ProjectCode || (row && row.xmbh) || '')
      if (!xmbh) {
        this.$message.warning('缺少项目编号')
        return
      }
      this.$router.push({ path: '/soil/handover', query: { xmbh } })
    },
    openPickAudit(row) {
      if (!this.canAudit(row)) {
        this.$message.warning('采样进行中，结束后 App 提交才可审核')
        return
      }
      const p = this.project || {}
      const xmbh = String(p.xmbh || p.ProjectCode || row.xmbh || '')
      // 必须用 App 提交时的 taskId（pending 记录），不能用误绑的监测项 id
      let tid = String(row.pickRecordTaskId || '')
      if (!tid && this.pendingByTask[row.id]) tid = String(row.id)
      if (!tid) {
        const hit = Object.keys(this.pendingByTask || {}).find(k => {
          const it = this.pendingByTask[k]
          return it && String(it.xmbh || '') === xmbh &&
            (!row.inspectSubType || String(it.inspectType || '') === String(row.inspectSubType))
        })
        if (hit) tid = hit
      }
      if (!tid) tid = String(row.id || '')
      if (!tid) {
        this.$message.error('缺少 App 任务 taskId，无法加载采样数据')
        return
      }
      this.$router.push({
        path: '/pick/input',
        query: {
          xmbh,
          taskId: tid,
          name: p.ProjectName || p.xmmc || row.xmmc || '',
          inspectType: row.inspectSubType || (this.pendingByTask[tid] && this.pendingByTask[tid].inspectType) || '',
          mode: 'audit'
        }
      })
    },
    async loadPendingAudit() {
      try {
        const xmbh = String((this.project && (this.project.xmbh || this.project.ProjectCode)) || '')
        const [pendingRes, auditedRes] = await Promise.all([
          api.get('yn_pick_pending'),
          xmbh ? api.get('yn_pick_audited', { xmbh }) : Promise.resolve({ data: { items: [] } })
        ])
        const items = (pendingRes.data && pendingRes.data.items) || []
        const auditedItems = (auditedRes.data && auditedRes.data.items) || []
        const map = {}
        items.forEach(it => {
          const tid = String(it.taskId || it.task_id || '')
          if (!tid) return
          if (xmbh && String(it.xmbh || '') !== xmbh) return
          map[tid] = it
        })
        const auditedMap = {}
        auditedItems.forEach(it => {
          const tid = String(it.taskId || it.task_id || '')
          if (!tid) return
          auditedMap[tid] = it
        })
        // 同项目行上已有 pickStatus 的也认
        ;(this.rows || []).forEach(r => {
          if (!r || !r.id) return
          if (r.pickStatus === 'pending_audit') {
            map[String(r.id)] = map[String(r.id)] || {
              taskId: r.id,
              xmbh: r.xmbh,
              inspectType: r.inspectSubType,
              place: r.monitorPlace,
              itemNames: r.inspectItems,
              submitter_xm: r.pickCharge,
              status: 'pending_audit'
            }
          }
          if (r.pickStatus === 'audited') {
            auditedMap[String(r.id)] = auditedMap[String(r.id)] || {
              taskId: r.id,
              xmbh: r.xmbh,
              inspectType: r.inspectSubType,
              place: r.monitorPlace,
              itemNames: r.inspectItems,
              submitter_xm: r.pickCharge,
              status: 'audited'
            }
          }
        })
        this.pendingByTask = map
        this.auditedByTask = auditedMap
        const seen = {}
        ;(this.rows || []).forEach(r => { if (r && r.id) seen[String(r.id)] = true })
        const prevOrphanSel = {}
        ;(this.orphanPendingRows || []).forEach(r => {
          if (r && r.id && r.selected) prevOrphanSel[String(r.id)] = true
        })
        const overrides = loadPickOverrides()
        const orphans = []
        Object.keys(map).forEach(tid => {
          if (seen[tid]) return
          const it = map[tid]
          const ox = it.xmbh || xmbh
          const ov = (overrides[`${ox}::${tid}`] || overrides[tid] || {})
          orphans.push({
            id: tid,
            xmbh: ox,
            inspectSubType: it.inspectType || '',
            inspectItems: it.itemNames || '',
            monitorPlace: it.place || '',
            pickCharge: it.submitter_xm || '',
            pickMembers: '',
            pickStaff: it.submitter_xm || '',
            pickDateLabel: '',
            instruments: ov.cyyq || '',
            vehiclePlate: ov.cph || '',
            frequency: '',
            status: '待审核',
            pickStatus: 'pending_audit',
            selected: !!prevOrphanSel[tid],
            fromPending: true,
            sj: { cyyq: ov.cyyq || '', cph: ov.cph || '' }
          })
        })
        Object.keys(auditedMap).forEach(tid => {
          if (seen[tid] || map[tid]) return
          const it = auditedMap[tid]
          const ox = it.xmbh || xmbh
          const ov = (overrides[`${ox}::${tid}`] || overrides[tid] || {})
          orphans.push({
            id: tid,
            xmbh: ox,
            inspectSubType: it.inspectType || '',
            inspectItems: it.itemNames || '',
            monitorPlace: it.place || '',
            pickCharge: it.submitter_xm || '',
            pickMembers: '',
            pickStaff: it.submitter_xm || '',
            pickDateLabel: '',
            instruments: ov.cyyq || '',
            vehiclePlate: ov.cph || '',
            frequency: '',
            status: '已完成采样',
            pickStatus: 'audited',
            selected: !!prevOrphanSel[tid],
            fromAudited: true,
            sj: { cyyq: ov.cyyq || '', cph: ov.cph || '' }
          })
        })
        this.orphanPendingRows = orphans
        const fp = Object.keys(map).sort().join(',') + '#' + Object.keys(auditedMap).sort().join(',')
        if (this._pendingFp && this._pendingFp !== fp) {
          pingTodoRefresh()
        }
        this._pendingFp = fp
      } catch (e) {
        this.pendingByTask = {}
        this.auditedByTask = {}
        this.orphanPendingRows = []
      }
    },
    async loadUsers() {
      try {
        const res = await api.get('yn_yh_list')
        this.users = (res.data || []).filter(u => u.zt == 1 && isPickSampler(u))
      } catch (e) {
        this.users = []
      }
    },
    ensureSelected() {
      const hadLocked = (this.displayRows || []).some(r => r && r.selected && this.isArrangeLocked(r))
      // 已完成采样的勾选无效
      ;(this.displayRows || []).forEach(r => {
        if (r && r.selected && this.isArrangeLocked(r)) r.selected = false
      })
      if (!this.selected.length) {
        this.$message.warning(hadLocked ? '已完成采样的任务不可再改安排信息' : '请先勾选需要设置的采样任务！')
        return false
      }
      return true
    },
    isBatchDialogOpen() {
      return !!(this.memberDlg || this.dateDlg || this.instDlg || this.vehicleDlg)
    },
    lockBatchTargets() {
      this.batchTargetIds = this.selected.map(r => String(r.id))
      return this.batchTargetIds.length > 0
    },
    resolveBatchTargets() {
      const ids = new Set((this.batchTargetIds || []).map(String))
      if (!ids.size) return []
      return (this.displayRows || []).filter(r => r && ids.has(String(r.id)) && !this.isArrangeLocked(r))
    },
    openMembers() {
      if (!this.ensureSelected()) return
      this.lockBatchTargets()
      this.batchMembers = []
      this.memberDlg = true
    },
    openDate() {
      if (!this.ensureSelected()) return
      this.lockBatchTargets()
      this.batchDate = null
      this.dateDlg = true
    },
    openInst() {
      if (!this.ensureSelected()) return
      this.lockBatchTargets()
      this.instDlg = true
    },
    openVehicle() {
      if (!this.ensureSelected()) return
      this.lockBatchTargets()
      this.batchPlate = ''
      this.vehicleDlg = true
    },
    onPickDlgClosed() {
      ;['memberSelect', 'vehicleSelect'].forEach((name) => {
        const sel = this.$refs[name]
        if (sel && typeof sel.blur === 'function') sel.blur()
        if (sel && sel.visible) sel.visible = false
      })
      try {
        document.querySelectorAll('body > .el-select-dropdown').forEach((el) => {
          if (el && el.parentNode) el.parentNode.removeChild(el)
        })
      } catch (e) { /* ignore */ }
    },
    async confirmMembers() {
      if (!this.batchMembers.length) return this.$message.warning('请选择采样组员')
      const targets = this.resolveBatchTargets()
      if (!targets.length) return this.$message.warning('勾选已失效，请重新勾选任务后再设置')
      const picked = this.batchMembers
        .map(id => this.users.find(x => String(x.id) === String(id)))
        .filter(Boolean)
      if (!picked.length) return this.$message.warning('用户无效')
      const text = picked.map(u => u.xm).join(';')
      const ids = picked.map(u => String(u.id))
      this.memberDlg = false
      this.saving = true
      try {
        for (const row of targets) {
          await patchPickFields(row, { cyry: text, cyry_ids: ids })
          this.$set(row, 'pickMembers', text)
          this.$set(row, 'pickStaff', text)
          row.selected = true
        }
        this.$message.success('已保存（点提交后才会下发 App）')
      } finally {
        this.saving = false
      }
    },
    async confirmDate() {
      if (!this.batchDate || !this.batchDate.length) return this.$message.warning('请选择日期')
      const targets = this.resolveBatchTargets()
      if (!targets.length) return this.$message.warning('勾选已失效，请重新勾选任务后再设置')
      const [from, to] = this.batchDate
      this.dateDlg = false
      this.saving = true
      try {
        for (const row of targets) {
          await patchPickFields(row, { cyrq_from: from, cyrq_to: to || from, yqwcsj: from })
          const pad = (d) => {
            const s = String(d).slice(0, 10)
            return `${s.slice(5, 7)}月${s.slice(8, 10)}日`
          }
          const label = (!to || to === from) ? pad(from) : `${pad(from)}~${pad(to)}`
          this.$set(row, 'pickDateFrom', from)
          this.$set(row, 'pickDateTo', to || from)
          this.$set(row, 'pickDateLabel', label)
          row.selected = true
        }
        this.$message.success('已成功保存')
      } finally {
        this.saving = false
      }
    },
    async onInstPicked(payload) {
      const text = formatPickedInstruments(payload)
      if (!text) return
      const targets = this.resolveBatchTargets()
      if (!targets.length) return this.$message.warning('勾选已失效，请重新勾选任务后再设置')
      this.saving = true
      try {
        for (const row of targets) {
          await patchPickFields(row, { cyyq: text })
          this.$set(row, 'instruments', text)
          row.selected = true
        }
        this.$message.success('已成功保存')
      } finally {
        this.saving = false
      }
    },
    async confirmVehicle() {
      const plate = String(this.batchPlate || '').trim()
      if (!plate) return this.$message.warning('请选择或输入车牌号')
      const targets = this.resolveBatchTargets()
      if (!targets.length) return this.$message.warning('勾选已失效，请重新勾选任务后再设置')
      this.vehicleDlg = false
      this.saving = true
      try {
        for (const row of targets) {
          await patchPickFields(row, { cph: plate })
          this.$set(row, 'vehiclePlate', plate)
          row.selected = true
        }
        this.$message.success('已成功保存')
      } finally {
        this.saving = false
      }
    },
    async submitSelected() {
      if (!this.ensureSelected()) return
      const gate = validatePickArrangeReady(this.selected)
      if (!gate.ok) {
        this.$message.warning(gate.message || '请先完善安排信息')
        return
      }
      try {
        await this.$confirm(`确认提交 ${this.selected.length} 条采样任务？`, '提交确认', { type: 'warning' })
      } catch (e) {
        return
      }
      this.saving = true
      try {
        const results = await markPickSubmitted(this.selected)
        const bad = (results || []).find(r => r && r.ok === 0)
        if (bad) {
          this.$message.error(bad.error || '同步 App 失败')
          return
        }
        this.selected.forEach(r => { r.status = '已提交'; r.selected = false })
        this.$message.success('已提交，采样员可在 App「采样信息」看到')
        this.$emit('submitted')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.pick-embed {
  background: #fff;
  min-height: 100%;
  padding: 12px;
}
.pick-embed__title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
}
.pick-embed__title a {
  color: #337ab7;
}
.pick-embed__batch {
  margin-bottom: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}
.pick-embed__table {
  background: #fff;
  margin-bottom: 0;
}
.pick-embed__cell-wrap {
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
